import React, { useContext, useEffect, useState } from 'react';
import { withTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LoaderContext, PollerContext, RegulationContext } from '../../../App';
import { AIReportJobStatus, currentYear, DEFAULT_ITEMS_PER_PAGE, LS_AUTHORITY_KEY, PollerType, startYear, STATUS } from '../../../core/constants/common';
import { Badge, Breadcrumb, Dropdown, Tab, Table, Tabs } from 'react-bootstrap';
import Pagination from "../../../components/Pagination/Pagination";
import { BiCalendar, BiDotsHorizontalRounded, BiPencil, BiPlus, BiTrash, BiUser } from 'react-icons/bi';
import NewReportModal from '../Reporting/NewReportModal';
import ReportService from '../../../core/service/ReportService';
import TruncatedText from '../../../core/utilities/TruncatedText';
import { FcFinePrint } from 'react-icons/fc';
import Utils from '../../../core/utilities/Utils';
import moment from "moment";
import GRI_pic from "../../../images/GRI.jpg";
import esg_ai_pic from "../../../images/esg_ai_Green.png";
import CompanyService from '../../../core/service/CompanyService';
import useHasPermission from '../../../core/hooks/useHasPermission';
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal';
import { toast } from "react-toastify";
import { BsEye, BsFileEarmarkPdf, BsFileEarmarkText } from 'react-icons/bs';
import ReportExportModal from '../Reporting/ReportExportModal';

const UAEFDLReport = ({ t }) => {
    const navigate = useNavigate();
  
    const [searchParams] = useSearchParams();
    const [passYear, setPassYear] = useState(searchParams.get('year') ?? null);
  

    const { setLoaderDisplay } = useContext(LoaderContext);
    const { glbRegulation } = useContext(RegulationContext);
    const [itemsTotalCount, setItemTotalCount] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_ITEMS_PER_PAGE);
    const [currentPage, setCurrentPage] = useState(1);
    const [regulation, setRegulation] = useState({});
    const [reports, setReports] = useState([]);
    const [editedReport, setEditedReport] = useState('');
    const [viewMode, setViewMode] = useState('LIST');
    const { pollerData } = useContext(PollerContext);
    const [activeTab, setActiveTab] = useState(1);
    const [businessUnits, setBusinessUnits] = useState([]);
    const { hasGHGReportUpdatePermission } = useHasPermission();
    const [reportIdToFinalize, setReportIdToFinalize] = useState(null);
    const [reportIdToDelete, setReportIdToDelete] = useState(null);
    const [reportToExport, setReportToExport] = useState({ id: 0 });

    useEffect(() => {
        listReports(glbRegulation.id, 1, DEFAULT_ITEMS_PER_PAGE, activeTab);
        setRegulation(glbRegulation);
        loadBusinessUnits();
    }, [glbRegulation?.id, activeTab]);

    const year = new Date().getFullYear();
  
    const [filter, setFilter] = useState({
        year: passYear ?? `${year}`,
    });
    
    const yearOptions = [];
    for (let i = currentYear; i >= startYear; i--) {
        yearOptions.push(<option value={i} key={i}>{i}</option>);
    }
    const data = JSON.parse(localStorage.getItem("authority"));
    const companyId = data.profile.company.id;

    const listReports = (
        regulationId,
        pageNumber,
        itemsPerPageVal,
        status
    ) => {
        const requestedPage = pageNumber > 0 ? pageNumber : 1;
        let apiPageNumber = requestedPage - 1;
        if (pageNumber <= 0) {
            setCurrentPage(1);
            apiPageNumber = 0;
        }
        let perPage;
        if (itemsPerPageVal > 0) {
            perPage = itemsPerPageVal;
        } else {
            perPage = itemsPerPage;
        }
        let req = ReportService.listReports(
            apiPageNumber,
            perPage,
            regulationId,
            companyId,
            status,
            ['USER', 'AI'].toString(),
            '',
            'UAE_FDL'
        );
        req?.then((response) => {
            const reportData = Array.isArray(response.data) ? response.data : [];
            const totalCount = parseFloat(response.headers["x-total-count"]) || 0;
            setReports(reportData);
            setItemTotalCount(totalCount);

            if (reportData.length === 0 && totalCount > 0 && requestedPage > 1) {
                const previousPage = requestedPage - 1;
                setCurrentPage(previousPage);
                listReports(regulationId, previousPage, perPage, status);
            }
        }).catch((error) => {
        });
    };

    const getPageRequest = (pageId, period, regulationId, reportTitle) => {
        if (reportTitle === null) {
            return ReportService.getReportTemplate({
                pageId: pageId,
                type: 'UAE_FDL',
                standardId: regulationId,
                period: period
            });
        } else {
            return ReportService.getReportTemplateData({
                pageId: pageId,
                standardId: regulationId,
                period: period,
                title: reportTitle,
                type: 'UAE_FDL'
            });
        }
    };

    const generatePDF = (report, openPreview = false) => {
        setLoaderDisplay(true);
        ReportService.generateESRSPDF(report.id)
            .then((res) => {
                setLoaderDisplay(false);
                let blob = new Blob([res.data], { type: 'application/pdf' });
                // Creating new object of PDF file
                const fileURL = window.URL.createObjectURL(blob);
                let link = document.createElement('a');
                link.href = fileURL;
                if (openPreview) {
                    link.target = '_blank';
                } else {
                    link.download = `${report.name}.pdf`;
                }
                link.click();
            })
            .catch((error) => {
                setLoaderDisplay(false);
                toast.error(error.title, {
                    position: toast.POSITION.TOP_RIGHT,
                });
            });
    };

    const finalizeReport = (id) => {
        setLoaderDisplay(true);
        ReportService.finalizeESRSReport(id)
            .then((res) => {
                if (res.status !== 200) return;
                setLoaderDisplay(false);
                toast.success(t("ReportFinalizedSuccessfully.label"), {
                    position: toast.POSITION.TOP_RIGHT,
                });
                listReports(glbRegulation.id, currentPage, itemsPerPage, activeTab);
            })
            .catch((error) => {
                setLoaderDisplay(false);
                toast.error(error.title, {
                    position: toast.POSITION.TOP_RIGHT,
                });
            });
    };

    const deleteReport = (id) => {
        setLoaderDisplay(true);
        ReportService.deleteESRSReport(id)
            .then((res) => {
                if (res.status < 200 || res.status >= 300) {
                    setLoaderDisplay(false);
                    return;
                }
                setLoaderDisplay(false);
                toast.success(t("ReportDeletedSuccessfully.label"), {
                    position: toast.POSITION.TOP_RIGHT,
                });
                const pageToLoad = reports.length <= 1 && currentPage > 1
                    ? currentPage - 1
                    : currentPage;
                if (pageToLoad !== currentPage) {
                    setCurrentPage(pageToLoad);
                }
                listReports(glbRegulation.id, pageToLoad, itemsPerPage, activeTab);
            })
            .catch((error) => {
                setLoaderDisplay(false);
                toast.error(error.title, {
                    position: toast.POSITION.TOP_RIGHT,
                });
            });
    };

    const renderActions = () => {
        return (
            <div>
                <div className="List_Table_01 mx-2">
                    <div className="List_table-card01_head p-0">
                        <button
                            type="button"
                            className="btn btn-primary mx-2"
                            disabled={pollerData.type === PollerType.REPORT_BUILDER &&
                                (pollerData.status === AIReportJobStatus.IN_PROGRESS || pollerData.status === AIReportJobStatus.NOT_STARTED)}
                            onClick={() => {
                                setEditedReport({ id: 0 });
                            }}>
                            <BiPlus /> {t("New.label")}
                        </button>

                        {/* <div className="List_table-card_Assessment ms-2">
                            <Form.Check
                                inline
                                label={<BsFillGridFill />}
                                type="radio"
                                id="viewMode1"
                                value={'GRID'}
                                checked={viewMode === 'GRID' ? "checked" : ''}
                                onChange={() => setViewMode('GRID')} />
                            <Form.Check
                                inline
                                label={<FaList />}
                                type="radio"
                                id="viewMode2"
                                value={'LIST'}
                                checked={viewMode === 'LIST' ? "checked" : ''}
                                onChange={() => setViewMode('LIST')} />
                        </div> */}
                    </div>
                </div>
            </div>
        );
    };

    const renderListView = () => {
        return (
            <Table>
                <thead>
                    <tr>
                        <th className="task-title-col">{t("ReportName.label")}</th>
                        <th>{t("Type.label")}</th>
                        <th>{t("Entities.label")}</th>
                        <th>{t("ReportingPeriod.label")}</th>
                        <th>{t("LastModified.label")}</th>
                        <th>{t("CreatedBy.label")}</th>
                        <th></th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {reports?.length > 0 ? (
                        reports?.map((report, index) => (
                            <tr key={report.id}>
                                <td className="task-title-col">
                                    <div className="d-flex align-items-center">
                                        {<TruncatedText text={report.name}></TruncatedText>}
                                        {
                                            report.type === 'AI' &&
                                            <span className="AI_list_Icon">
                                                <img src={esg_ai_pic} />
                                            </span>
                                        }
                                    </div>
                                </td>
                                <td>
                                    {report.basis === 'ENTITY' ? 'Entity Report' :
                                        report.basis === 'ORG' ? 'Organization Report' :
                                            report.basis || '-'}
                                </td>
                                <td className="white-nowrap">{getEntityNames(report.entities)}</td>
                                <td className="white-nowrap">
                                    {report.period && report.periodEnd
                                        ? (() => {
                                            const startDate = moment(report.period);
                                            const endDate = moment(report.periodEnd);
                                            if (startDate.isValid() && endDate.isValid()) {
                                                return `${startDate.format("MMMM YYYY")}-${endDate.format("MMMM YYYY")}`;
                                            } else {
                                                return '-';
                                            }
                                        })()
                                        : report.period
                                            ? (() => {
                                                const date = moment(report.period);
                                                return date.isValid() ? date.format("MMM DD, YYYY") : '-';
                                            })()
                                            : '-'
                                    }
                                </td>
                                <td> {report.lastModifiedDate ? moment(report.lastModifiedDate).format("MMM DD, YYYY") : ""}</td>
                                <td>{report.creator?.name ?? report?.createdBy}</td>

                                <td>
                                    {(() => {

                                        switch (report.status) {
                                            case "DRAFT":
                                                return (
                                                    <button className="btn btn-primary btn-sm" hidden={!(hasGHGReportUpdatePermission())}
                                                        onClick={() => {
                                                            setReportIdToFinalize(report.id)
                                                        }
                                                        }>{t('Finalize.label')}</button>
                                                );
                                        }
                                    })()}
                                </td>
                                <td>
                                    {renderStudioBasedReportMoreOptions(report)}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td colSpan="6" className="text-center">
                                <div className="Nodatafound_box">
                                    <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                                </div>
                            </td>
                        </tr>
                    )}
                </tbody>
            </Table>
        );
    };

    const renderGridView = () => {
        return (
            <div className="Reporting_Studio_gridbox">
                <div className="row m-0">
                    {
                        reports.length === 0 &&
                        <div className="Nodatafound_box">
                            <h6><FcFinePrint className="me-2" /> {t("Nodatafound.label")}</h6>
                        </div>
                    }
                    {
                        reports?.map((report, index) => {
                            return (
                                <div className='col-md-3 p-2'
                                    key={`reportCard-${index + 1}`}>
                                    <div className={`card Report_Studio_Singleview ${report.status}`}>
                                        <div className="card-body">
                                            <span className="	Reporting_Period">
                                                {Utils.getYearFrom(report.period)}
                                            </span>
                                            <div className="Report_pic">
                                                <img src={GRI_pic} alt="Report Logo" />
                                                <span className="Reporting_StatusBox">
                                                    {(() => {
                                                        switch (report.status) {
                                                            case "FINALIZED":
                                                                return (<Badge bg="badge-Finalized">{STATUS['FINALIZED']}</Badge>);
                                                            case "FAILED":
                                                                return (<Badge bg="badge-Danger">{STATUS['FAILED']}</Badge>);
                                                            case "DRAFT":
                                                            default:
                                                                if (report.type === 'AI' && !report.active) {
                                                                    return (
                                                                        <Badge bg="badge-Draft">
                                                                            {STATUS['GENERATING_REPORT']}
                                                                        </Badge>
                                                                    );
                                                                } else {
                                                                    return (
                                                                        <Badge bg={
                                                                            report.status === 'DRAFT' ? 'badge-Inactive' :
                                                                                report.status === 'GENERATING_REPORT' ? 'badge-Inprogress' :
                                                                                    'badge-New'
                                                                        }>
                                                                            {STATUS[report.status]}
                                                                        </Badge>
                                                                    );
                                                                }
                                                        }
                                                    })()}
                                                </span>
                                            </div>
                                            <div className="RS_Singleview_dt">

                                                <div className="RS_Singleview_dt_in">
                                                    <h6>
                                                        <TruncatedText text={report.title}></TruncatedText>
                                                    </h6>
                                                    <ul>
                                                        <li>
                                                            <BiUser /> {report.creator?.name ?? report?.createdBy}
                                                        </li>
                                                        <li> / </li>
                                                        <li>
                                                            <BiCalendar /> {moment(report?.lastModifiedDate).format('MMM DD, YYYY')}
                                                        </li>
                                                    </ul>
                                                    <span className="more_options">
                                                        {renderStudioBasedReportMoreOptions(report)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })
                    }
                    {itemsTotalCount > 10 ? (
                        <div className="p-2 d-flex align-items-center justify-content-end">
                            <Pagination
                                itemsTotalCount={itemsTotalCount}
                                itemsPerPage={itemsPerPage}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                alwaysShown={true}
                                setItemsPerPage={setItemsPerPage}
                                pageClicked={(pageNum, perPage) => {
                                    listReports(regulation.id, pageNum, perPage);
                                }}
                            />
                        </div>
                    ) : null}

                </div>
            </div>
        );
    };

    const renderTabContent = () => {
        return (
            <div className="List_Table_01">
                <div className={`List_table-card01 ${viewMode === 'LIST' ? 'card' : ''}`}>
                    {
                        viewMode === 'LIST' &&
                        <>
                            {renderListView()}
                            {
                                itemsTotalCount > 10 &&
                                <div className="p-2 List_footer_count">
                                    <Pagination
                                        itemsTotalCount={itemsTotalCount}
                                        itemsPerPage={itemsPerPage}
                                        currentPage={currentPage}
                                        setCurrentPage={setCurrentPage}
                                        alwaysShown={true}
                                        setItemsPerPage={setItemsPerPage}
                                        pageClicked={(pageNum, perPage) => {
                                            listReports(regulation.id, pageNum, perPage);
                                        }}
                                    />
                                </div>
                            }
                        </>
                    }
                    {viewMode === 'GRID' && renderGridView()}
                </div>
            </div>
        );
    };

    const renderStudioBasedReportMoreOptions = (report) => {
        return (
            <Dropdown>
                <Dropdown.Toggle
                    variant="blank_outline"
                    id="task-list-dropdown">
                    <BiDotsHorizontalRounded />
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {
                        report.status !== "FINALIZED"
                            ?
                            <Dropdown.Item onClick={() => onEditReport(report)}>
                                <BiPencil className="me-1" /> {t("Edit.label")}
                            </Dropdown.Item>
                            : <></>
                    }
                    {report.status != "NOT_STARTED" && (<>
                        <Dropdown.Item onClick={() => generatePDF(report, true)}>
                            <BsEye className="me-1" /> {t("Preview.label")}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => generatePDF(report)}>
                            <BsFileEarmarkPdf className="me-1" /> {t("DownloadAsPDF.label")}
                        </Dropdown.Item>
                        <Dropdown.Item onClick={() => setReportToExport({ id: report.id, exportAs: 'WORD' })}>
                            <BsFileEarmarkText className="me-1" /> {t("DownloadAsWord.label")}
                        </Dropdown.Item>
                    </>)}
                    {
                        (report.status === 'DRAFT' || report.status === 'NOT_STARTED')
                            ?
                            <Dropdown.Item onClick={() => setReportIdToDelete(report.id)}>
                                <BiTrash className="me-1" /> {t("Delete.label")}
                            </Dropdown.Item>
                            : <></>
                    }
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    const onEditReport = (report) => {
        if (report?.status === 'DRAFT') {
            setLoaderDisplay(true);
            let periodEnd = report.periodEnd;
            if (!periodEnd) {
                const parsedYear = Utils.getYearFrom(report.period);
                periodEnd = `${parsedYear}-12-31`;
            }
            ReportService.getDataCompletionProgress(glbRegulation?.id,
                moment(report.period).format('YYYY-MM-DD'), true,
                report.period, periodEnd, report.entities).then(res => {
                    setLoaderDisplay(false);
                    if (res.status !== 200) {
                        setEditedReport(report);
                        return;
                    }
                    let changed = false;
                    for (let index = 0; index < res.data.data?.length; index++) {
                        const element = res.data.data[index];
                        const inboundDate = moment(element?.lastModifiedDate);
                        const reportDate = moment(report?.lastModifiedDate);
                        if (inboundDate.isAfter(reportDate)) {
                            changed = true;
                            break;
                        }
                    }
                    if (changed) {
                        setEditedReport({ ...report, dataCompletionProgress: res.data.data });
                    } else {
                        // Fetch individual report details to get the job ID
                        ReportService.getReport(report.id)
                            .then(reportResponse => {
                                if (reportResponse.status === 200) {
                                    const fullReport = reportResponse.data;
                                    const jobId = getJobIdFromReport(fullReport);

                                    navigate('/company_admin/reporting?id=' + report.id
                                        + '&mode=EDIT&regulationId=' + regulation.id
                                        + '&stdType=UAE_FDL'
                                        + '&year=' + Utils.getYearFrom(report.period)
                                        + '&jobId=' + jobId
                                        + '&activeTab=' + activeTab
                                        + '&source=ghg-report',
                                        { state: null });
                                } else {
                                    // Fallback to navigation without job ID
                                    navigate('/company_admin/reporting?id=' + report.id
                                        + '&mode=EDIT&regulationId=' + regulation.id
                                        + '&stdType=UAE_FDL'
                                        + '&year=' + Utils.getYearFrom(report.period)
                                        + '&activeTab=' + activeTab
                                        + '&source=ghg-report',
                                        { state: null });
                                }
                            })
                            .catch(error => {
                                // Fallback to navigation without job ID
                                navigate('/company_admin/reporting?id=' + report.id
                                    + '&mode=EDIT&regulationId=' + regulation.id
                                    + '&stdType=UAE_FDL'
                                    + '&year=' + Utils.getYearFrom(report.period)
                                    + '&activeTab=' + activeTab
                                    + '&source=ghg-report',
                                    { state: null });
                            });
                    }
                }).catch(error => {
                    setLoaderDisplay(false);
                    setEditedReport(report);
                })
        } else {
            setEditedReport(report);
        }
    };

    // Helper function to get job ID from report data
    const getJobIdFromReport = (report) => {
        // First try to get from report.job.id
        if (report?.job?.id) {
            return report.job.id;
        }

        // Fallback to report.Jobid (if it exists and is not null)
        if (report?.Jobid) {
            return report.Jobid;
        }

        // Try other possible field names
        if (report?.jobId) {
            return report.jobId;
        }

        if (report?.job_id) {
            return report.job_id;
        }

        // If still no job ID, try to get from localStorage
        const reportData = JSON.parse(localStorage.getItem('currentReport') || '{}');
        if (reportData?.job?.id) {
            return reportData.job.id;
        }

        return null;
    };

    const callbackFinalizeReport = (boolVal) => {
        setReportIdToFinalize(0);
        boolVal && finalizeReport(reportIdToFinalize);
    };

    const callbackDeleteReport = (boolVal, id) => {
        const deleteId = id || reportIdToDelete;
        setReportIdToDelete(null);
        if (boolVal && deleteId) {
            deleteReport(deleteId);
        }
    };

    const loadBusinessUnits = async () => {
        try {
            const res = await CompanyService.getEnterprisesByLevel(1);
            if (res.status === 200) {
                const sortedBusinessUnits = res.data.sort((a, b) => a.sortOrder - b.sortOrder);
                setBusinessUnits(sortedBusinessUnits);
            }
        } catch (error) {
            console.error('Error loading business units:', error);
        }
    };

    const getEntityNames = (entities) => {
        if (!entities || !Array.isArray(entities) || entities.length === 0) return 'N/A';

        // If business units haven't loaded yet, show only names from entity objects
        if (businessUnits.length === 0) {
            return entities.map(entity =>
                typeof entity === 'object' ? entity.name : entity
            ).filter(name => name).join(', ') || 'N/A';
        }

        return entities.map(entity => {
            const entityId = typeof entity === 'object' ? entity.id : entity;
            const businessUnit = businessUnits.find(bu => bu.id === entityId);
            return businessUnit ? businessUnit.name : (typeof entity === 'object' ? entity.name : null);
        }).filter(name => name).join(', ') || 'N/A';
    };

    return (
        <>
            <div className="ReportingAndDisclosures_box">
                <div className="List_header">
                    <Breadcrumb className="h5 m-0">
                        <Breadcrumb.Item active>{t("UAEFDLReport.label")}</Breadcrumb.Item>
                    </Breadcrumb>
                    {renderActions()}
                </div>
                <div className="ReportingAndDisclosures_box_inner">
                    {/* {renderAIReportGenerationProgressCard()} */}
                    <div className="col-lg-12 p-1 ps-0 mt-1 report-list-container" id={regulation.id}>
                        {
                            <Tabs
                                id="reportingListTabs"
                                className="mb-1"
                                activeKey={activeTab}
                                onSelect={(k) => setActiveTab(+k)}>
                                <Tab eventKey={1} title={t("Finalized.label")}>{renderTabContent()}</Tab>
                                <Tab eventKey={0} title={t("Draft.label")}>{renderTabContent()}</Tab>
                                <Tab eventKey={2} title={t("NotStarted.label")}>{renderTabContent()}</Tab>
                                <Tab eventKey={3} title={t("Failed.label")}>{renderTabContent()}</Tab>
                            </Tabs>
                        }
                    </div>
                </div>
                {
                    editedReport.id >= 0 &&
                    <NewReportModal onHide={() => setEditedReport('')}
                        editedReport={editedReport}
                        view={'UAE_FDL'}
                        activeView={'UAE_FDL'}
                        reportGenerationCallback={() => {
                            listReports(glbRegulation.id, 1, DEFAULT_ITEMS_PER_PAGE)
                        }} />
                }
                {
                    reportIdToFinalize > 0 && (
                        <ConfirmationModal
                            modalShow={true}
                            messageText={t("FinalizeReportModal.label")}
                            callbackModal={callbackFinalizeReport}
                            confirmBtn={t("Yes.label")}
                            CancelBtn={t("No.label")}
                        />
                    )
                }
                {
                    reportIdToDelete > 0 && (
                        <ConfirmationModal
                            modalShow={true}
                            messageText={t("DeleteReportModal.label")}
                            callbackModal={callbackDeleteReport}
                            id={reportIdToDelete}
                            confirmBtn={t("Yes.label")}
                            CancelBtn={t("No.label")}
                        />
                    )
                }
                {
                    reportToExport?.id > 0 &&
                    <ReportExportModal
                        reportId={reportToExport?.id}
                        exportAs={reportToExport?.exportAs}
                        onHide={() => setReportToExport({ id: 0 })}
                        activeView={'UAE_FDL'}
                    />
                }
            </div>
        </>
    );
}

export default withTranslation()(UAEFDLReport);